import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Settings, SlidersHorizontal } from 'lucide-react';
import { LawReference } from './LawReference';
import { useComplianceCountry } from '../../../../hooks/useComplianceCountry';
import type { StatutoryBaseHealthView } from '../../../../models/payroll';

export interface StatutoryBaseAlertProps {
  health: StatutoryBaseHealthView | null | undefined;
  className?: string;
}

/**
 * CO-256 (Finding 3) — grouping and decimal separators follow the ACTIVE locale, not en-US: this
 * money line sits inside an otherwise fully translated Arabic/French sentence, and "1,234.50" in
 * the middle of French copy that everywhere else writes "1 234,50" reads as a foreign paste.
 *
 * `-u-nu-latn` pins the DIGITS to Latin on purpose. Arabic defaults to Arabic-Indic digits, but the
 * same sentence also carries `{{percent}}`, which i18next interpolates as a plain number — so a bare
 * 'ar' tag would put ١٬٢٣٤ and 23.5 side by side in one sentence. One digit system, localized
 * separators.
 */
const localeTag = (lang: string | undefined): string => `${lang || 'en'}-u-nu-latn`;

const money = (value: number, lang?: string): string =>
  value.toLocaleString(localeTag(lang), { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Exported (CO-261): the Overview tab's GOSI card renders the SAME
 * `underContributionPercent` and was interpolating it raw, so an ar/fr tenant read "23,5 %" in
 * this alert and "23.5%" in the card immediately above it — one figure, two number systems, on
 * one screen. One formatter, one reading.
 */
export const percent = (value: number, lang?: string): string =>
  value.toLocaleString(localeTag(lang), { maximumFractionDigits: 2 });

/**
 * 🔴 CO-256 — the statutory-base guard, on screen.
 *
 * WHY IT LOOKS LIKE THIS. The guard already existed on the server and in the Event Log reason code
 * (StatutoryBaseModeZero), but the only user-facing copy lived inside the Gross Pay component edit
 * dialog, which HR opens only if they already suspect the problem. This renders the same fact where
 * HR actually monitors compliance, and it leads with the MONEY rather than with a configuration
 * adjective: a collapsed base looks like a valid, smaller number on every screen, so "misconfigured"
 * gets dismissed and "SAR 585 of statutory base is missing, that is GOSI arrears at audit" does not.
 *
 * Deliberately the existing red-alert idiom on these screens (full border + tinted surface + leading
 * icon + LawReference + inline fix links). No new visual vocabulary for an alert that must read as
 * the same class of problem as the ones beside it.
 */
export const StatutoryBaseAlert: React.FC<StatutoryBaseAlertProps> = ({
  health,
  className = '',
}) => {
  const { t, i18n } = useTranslation();
  const country = useComplianceCountry();

  /**
   * 🔴 CO-264 — KSA-ONLY BY CONSTRUCTION. Do not mount this component on a non-KSA tenant.
   *
   * The money line below hardcodes SAR (`payroll.statutoryBase.currencySar`, which renders 'SAR' /
   * 'ر.س' in every locale) and cites GOSI Law M/22. That is correct only because the sole mount
   * point today is `KsaGosiPanel.tsx:105`, which is itself KSA-guarded, and because the Overview
   * tab passes `statutoryBaseHealth` as null on UAE. `StatutoryBaseHealthView` carries no currency
   * of its own, so reusing this component anywhere else would print a KSA currency symbol against
   * another country's wage figures — a WRONG MONEY STATEMENT on a screen that tells HR what they
   * owe a regulator, not a cosmetic label bug.
   *
   * Enforced rather than merely documented: a non-KSA mount is a defect, so it fails loudly in dev
   * and renders nothing at all rather than lying about the currency in production.
   */
  if (country !== 'KSA') {
    // `process.env.NODE_ENV`, not `import.meta.env` — this project's tsconfig has no vite/client
    // types, so `import.meta.env` is a type error here. Same idiom as ErrorBoundary.tsx:61.
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error(
        `[StatutoryBaseAlert] mounted on complianceCountry="${country}". This component hardcodes SAR ` +
          'and GOSI Law M/22 and is valid for KSA only. Give StatutoryBaseHealthView a currency and ' +
          'a per-country law reference before reusing it.'
      );
    }
    return null;
  }

  if (!health || health.isHealthy) return null;

  const lang = i18n.language;
  const affected = health.affectedEmployeeCount;
  const nothingFlagged = health.basicSalaryConfigCount === 0;
  /* CO-256 (Finding 2) — the "counts only components flagged as the basic wage" clause is true ONLY
     for BasicOnly. IsSilentZeroBase is also raised for NoComponents, for an explicit flagged set
     summing to zero, and for ForcedGross over zero-amount components. `baseModeConsumesFlag` exists
     precisely to tell those apart, so the cause sentence branches on it instead of asserting the
     BasicOnly explanation for every mode. */
  const consumesFlag = health.baseModeConsumesFlag;
  /* GOSI is KSA-only, so the currency is SAR by construction — but the LABEL is translated rather
     than a hardcoded literal inside a translated sentence. (The old `currency` prop was dead: no
     caller ever passed it.)
     🔴 CO-264 — `currencySar` is a FIXED CURRENCY, not a tenant currency. It is safe only because
     of the `country !== 'KSA'` guard at the top of this component. If this alert ever needs to
     serve another jurisdiction, plumb the currency through `StatutoryBaseHealthView` from the
     server (which knows the tenant's payroll currency) — do NOT relax the guard and leave this key
     in place, or the money line states another country's arrears in riyals. */
  const currency = t('payroll.statutoryBase.currencySar', 'SAR');

  return (
    <div
      data-testid="statutory-base-alert"
      className={`p-4 rounded-lg bg-red-50 border border-red-200 text-red-800 ${className}`}
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" aria-hidden="true" />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p className="font-semibold">
              {/* `affected`, not `count`: `count` would engage i18next pluralisation and need
                  per-language _one/_other variants for a string that reads fine either way. */}
              {t('payroll.statutoryBase.alertTitle', {
                affected,
                defaultValue: 'Statutory wage base is zero for {{affected}} employee(s)',
              })}
            </p>
            <LawReference
              citations={[
                {
                  code: 'GOSI Law M/22 (1421H), Art. 19',
                  description: t(
                    'payroll.statutoryBase.lawRef',
                    'Social-insurance contributions are computed on the basic wage plus housing. Contributing on a lower base than the employee is actually paid leaves an arrears balance that GOSI recovers, with fines, at audit.'
                  ),
                },
              ]}
            />
          </div>

          <p className="text-sm mt-1">
            {!consumesFlag
              ? t('payroll.statutoryBase.causeModeIgnoresFlag', {
                  mode: health.baseMode,
                  defaultValue:
                    'The statutory base mode is {{mode}}, which does not read the basic-wage flag at all, so these employees still resolve to a base of zero — the pay components they hold carry no amount this mode counts. Check that each of them has an active pay component with a real amount before changing the flag.',
                })
              : nothingFlagged
              ? t('payroll.statutoryBase.causeNothingFlagged', {
                  mode: health.baseMode,
                  defaultValue:
                    'The statutory base mode is {{mode}}, which counts only pay components flagged as the basic salary, and no component in this company carries that flag. Every affected employee resolves to a base of zero while their pay components hold real amounts.',
                })
              : t('payroll.statutoryBase.causeNotFlaggedForEmployees', {
                  mode: health.baseMode,
                  defaultValue:
                    'The statutory base mode is {{mode}}, which counts only pay components flagged as the basic salary, and none of these employees is assigned a flagged component. Their amounts are not the problem.',
                })}
          </p>

          <p className="text-sm mt-2 font-medium tabular-nums">
            {t('payroll.statutoryBase.moneyLine', {
              currency,
              reported: money(health.reportedWageBaseTotal, lang),
              expected: money(health.expectedWageBaseTotal, lang),
              percent: percent(health.underContributionPercent, lang),
              defaultValue:
                'Contributions are being computed on {{currency}} {{reported}} instead of {{currency}} {{expected}}: {{percent}}% of the statutory base is missing. The shortfall accrues as GOSI arrears payable at audit, with the employee share unrecoverable once the month closes.',
            })}
          </p>

          <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 mt-2.5">
            <Link
              to="/settings/payroll/gross-pays"
              data-testid="statutory-base-fix-components"
              className="inline-flex items-center gap-1.5 text-sm font-medium underline"
            >
              <Settings className="w-3.5 h-3.5" aria-hidden="true" />
              {/* CO-256 (Finding 2) — "flag the basic salary component" is only the fix when the mode
                  actually reads the flag. Under a mode that ignores it, the fix is the amounts. */}
              {consumesFlag
                ? t('payroll.statutoryBase.fixComponentsCta', 'Flag the basic salary component')
                : t('payroll.statutoryBase.reviewComponentsCta', 'Review the pay components')}
            </Link>
            <Link
              to="/settings/payroll/compliance"
              data-testid="statutory-base-fix-mode"
              className="inline-flex items-center gap-1.5 text-sm font-medium underline"
            >
              <SlidersHorizontal className="w-3.5 h-3.5" aria-hidden="true" />
              {t('payroll.statutoryBase.fixModeCta', 'Or change the statutory base mode')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
